import React, { memo, useRef, useState } from 'react';
import { type IAgentBadgeData } from '../../services/agent-layout';
import { getCanvasBlurFilter } from '../../services/blur';
import { useCanvasBlur, useFramework, useMountEffect } from '../hooks';
import './edgeless-host.less';
import { BaseScaleView } from './base-scale-view';
import { CustomLayer } from './custom-layer-view';

type Props = {
    stagePlugins: React.ReactNode;
};

// Agent badge 高度估算，用于可视区域裁剪。
const AGENT_BADGE_HEIGHT = 40;
// Agent badge 与对应内容顶部的间距。
const AGENT_BADGE_BOTTOM_GAP = 16;
// Agent badge 左右内边距总和估算。
const AGENT_BADGE_HORIZONTAL_PADDING = 20;
// Agent badge 头像尺寸估算。
const AGENT_BADGE_AVATAR_SIZE = 32;
// Agent badge 头像与名称之间的间距估算。
const AGENT_BADGE_GAP = 6;
// Agent badge 单个字符宽度估算。
const AGENT_BADGE_CHAR_WIDTH = 14;

const getBadgeList = (badgeMap: Record<number, IAgentBadgeData[]>) =>
    Object.values(badgeMap).reduce<IAgentBadgeData[]>((list, badges) => list.concat(badges), []);

const syncDisplay = (node: HTMLElement, visible: boolean) => {
    const display = visible ? '' : 'none';
    if (node.style.display !== display) {
        node.style.display = display;
    }
};

const AgentGroupBorder: React.FC<{ badge: IAgentBadgeData; active: boolean }> = ({ badge, active }) => {
    const ref = useRef<HTMLDivElement>(null);
    const framework = useFramework();
    const { edgelessCanvas, editor, events } = framework.service;
    const bounds = active ? badge.groupBounds : badge.placeholderGroupBounds;

    const getViewState = React.useCallback(() => {
        if (!bounds) return null;

        const scale = edgelessCanvas.zoom;
        const x = bounds.left * scale + edgelessCanvas.translateX;
        const y = bounds.top * scale + edgelessCanvas.translateY;
        const width = bounds.width * scale;
        const height = bounds.height * scale;
        const { width: viewportWidth, height: viewportHeight } = editor.getBoundingClientRect();

        return {
            width,
            height,
            borderRadius: bounds.borderRadius * scale,
            transform: `translate(${x}px, ${y}px)`,
            visible: !(x + width <= 0 || x >= viewportWidth || y + height <= 0 || y >= viewportHeight),
        };
    }, [bounds, edgelessCanvas, editor]);

    const syncDom = React.useCallback(() => {
        const node = ref.current;
        const viewState = getViewState();
        if (!node || !viewState) return;

        const width = `${viewState.width}px`;
        const height = `${viewState.height}px`;
        const borderRadius = `${viewState.borderRadius}px`;
        if (node.style.width !== width) {
            node.style.width = width;
        }
        if (node.style.height !== height) {
            node.style.height = height;
        }
        if (node.style.borderRadius !== borderRadius) {
            node.style.borderRadius = borderRadius;
        }
        if (node.style.transform !== viewState.transform) {
            node.style.transform = viewState.transform;
        }
        syncDisplay(node, viewState.visible);
    }, [getViewState]);

    const syncTransform = React.useCallback(() => {
        const node = ref.current;
        const viewState = getViewState();
        if (!node || !viewState) return;

        if (node.style.transform !== viewState.transform) {
            node.style.transform = viewState.transform;
        }
        syncDisplay(node, viewState.visible);
    }, [getViewState]);

    React.useLayoutEffect(() => {
        syncDom();

        const zoomHandle = edgelessCanvas.onZoom(syncDom);
        const panHandle = edgelessCanvas.onPan(syncTransform);

        return () => {
            events.off(zoomHandle);
            events.off(panHandle);
        };
    }, [edgelessCanvas, events, syncDom, syncTransform]);

    if (!bounds) return null;

    const viewState = getViewState();
    return (
        <div
            ref={ref}
            className={`edgeless__agent-group-border${active ? ' is-active' : ''}`}
            style={
                viewState
                    ? {
                          width: viewState.width,
                          height: viewState.height,
                          borderRadius: viewState.borderRadius,
                          transform: viewState.transform,
                          display: viewState.visible ? undefined : 'none',
                      }
                    : undefined
            }
        />
    );
};

const AgentBadge: React.FC<{ badge: IAgentBadgeData }> = ({ badge }) => {
    const ref = useRef<HTMLDivElement>(null);
    const framework = useFramework();
    const { agentLayout, edgelessCanvas, editor, events } = framework.service;

    const getViewState = React.useCallback(() => {
        const scale = edgelessCanvas.zoom;
        // 放大时跟随画布缩放比例；缩小时不低于 1024px 基准值
        const badgeScale = agentLayout.getCanvasScale();
        const x = badge.left * scale + edgelessCanvas.translateX;
        const y =
            (badge.top - AGENT_BADGE_HEIGHT * badgeScale - AGENT_BADGE_BOTTOM_GAP * badgeScale) * scale +
            edgelessCanvas.translateY;
        const badgeWidth =
            (AGENT_BADGE_HORIZONTAL_PADDING +
                AGENT_BADGE_AVATAR_SIZE +
                AGENT_BADGE_GAP +
                badge.name.length * AGENT_BADGE_CHAR_WIDTH) *
            badgeScale *
            scale;
        const badgeHeight = AGENT_BADGE_HEIGHT * badgeScale * scale;
        const { width: viewportWidth, height: viewportHeight } = editor.getBoundingClientRect();

        return {
            transform: `translate(${x}px, ${y}px) scale(${scale * badgeScale})`,
            visible: !(x + badgeWidth <= 0 || x >= viewportWidth || y + badgeHeight <= 0 || y >= viewportHeight),
        };
    }, [agentLayout, badge, edgelessCanvas, editor]);

    const syncTransform = React.useCallback(() => {
        const node = ref.current;
        if (!node) return;

        const viewState = getViewState();
        if (node.style.transform !== viewState.transform) {
            node.style.transform = viewState.transform;
        }
        syncDisplay(node, viewState.visible);
    }, [getViewState]);

    React.useLayoutEffect(() => {
        syncTransform();

        const zoomHandle = edgelessCanvas.onZoom(syncTransform);
        const panHandle = edgelessCanvas.onPan(syncTransform);

        return () => {
            events.off(zoomHandle);
            events.off(panHandle);
        };
    }, [edgelessCanvas, events, syncTransform]);

    const viewState = getViewState();
    return (
        <div
            ref={ref}
            className="edgeless__agent-badge"
            style={{
                transform: viewState.transform,
                display: viewState.visible ? undefined : 'none',
            }}
        >
            {badge.avatar ? (
                <img className="edgeless__agent-badge-avatar" src={badge.avatar} alt={badge.name} />
            ) : (
                <div className="edgeless__agent-badge-avatar edgeless__agent-badge-avatar--placeholder" />
            )}
            <span className="edgeless__agent-badge-name">{badge.name}</span>
        </div>
    );
};

const AgentBadges: React.FC = () => {
    const framework = useFramework();
    const { agentLayout, events } = framework.service;
    const [badges, setBadges] = useState<IAgentBadgeData[]>(getBadgeList(agentLayout.badgeMap));
    const [activeGroupBorderKey, setActiveGroupBorderKey] = useState(agentLayout.activeGroupBorderKey);

    useMountEffect(() => {
        const handle = agentLayout.onBadgeMapChange((badgeMap) => {
            setBadges(getBadgeList(badgeMap));
        });
        return () => events.off(handle);
    });

    useMountEffect(() => {
        const handle = agentLayout.onActiveGroupBorderKeyChange(setActiveGroupBorderKey);
        return () => events.off(handle);
    });

    if (!badges.length) return null;

    return (
        <div className="edgeless__agent-badges">
            {badges.map((badge) => (
                <AgentGroupBorder
                    key={`${badge.key}:placeholder-border`}
                    badge={badge}
                    active={badge.key === activeGroupBorderKey}
                />
            ))}
            {badges.map((badge) => (
                <AgentBadge key={badge.key} badge={badge} />
            ))}
        </div>
    );
};

const DomRender: React.FC = React.memo(() => {
    const { edgelessCanvas, events, flatLayer, frame, behavior } = useFramework().service;
    const [domRenderList, setDomRenderList] = useState<Set<string>>(new Set());

    useMountEffect(() => {
        const handle = edgelessCanvas.onDomRender(({ object, show }) => {
            setDomRenderList((prev) => {
                if (show) {
                    return new Set([...prev, object.layerId]);
                } else {
                    prev.delete(object.layerId);
                    return new Set([...prev]);
                }
            });
        });
        return () => events.off(handle);
    });

    if (domRenderList.size === 0) return null;

    return (
        <div className="edgeless__dom">
            {Array.from(domRenderList).map((layerId) => {
                const layer = flatLayer.getLayer(layerId) || frame.getRuntimeLayer(layerId);
                if (!layer) return null;
                return (
                    <BaseScaleView key={layerId} layer={layer}>
                        {behavior.get(layer)?.render?.(layer) ?? null}
                    </BaseScaleView>
                );
            })}
        </div>
    );
});

const GridDot: React.FC = React.memo(() => {
    const ref = useRef<HTMLDivElement>(null);
    const framework = useFramework();
    const { edgelessCanvas, events } = framework.service;
    const gap = 14;

    const getViewState = React.useCallback(() => {
        const scale = edgelessCanvas.zoom;
        let zoom = Math.max(0.1, scale * 2);
        if (scale < 0.7) {
            zoom = zoom * 2;
        }

        return {
            backgroundImage: `radial-gradient(var(--edgeless-bg-dot-color) ${zoom}px, var(--edgeless-bg-color) ${zoom}px)`,
            backgroundPosition: `${edgelessCanvas.translateX}px ${edgelessCanvas.translateY}px`,
            backgroundSize: `${gap * zoom}px ${gap * zoom}px`,
            opacity: scale < 0.1 ? `${scale}` : '1',
        };
    }, [edgelessCanvas]);

    const syncDom = React.useCallback(() => {
        const node = ref.current;
        if (!node) return;

        const viewState = getViewState();
        if (node.style.backgroundImage !== viewState.backgroundImage) {
            node.style.backgroundImage = viewState.backgroundImage;
        }
        if (node.style.backgroundPosition !== viewState.backgroundPosition) {
            node.style.backgroundPosition = viewState.backgroundPosition;
        }
        if (node.style.backgroundSize !== viewState.backgroundSize) {
            node.style.backgroundSize = viewState.backgroundSize;
        }
        if (node.style.opacity !== viewState.opacity) {
            node.style.opacity = viewState.opacity;
        }
    }, [getViewState]);

    const syncPosition = React.useCallback(() => {
        const node = ref.current;
        if (!node) return;

        const backgroundPosition = `${edgelessCanvas.translateX}px ${edgelessCanvas.translateY}px`;
        if (node.style.backgroundPosition !== backgroundPosition) {
            node.style.backgroundPosition = backgroundPosition;
        }
    }, [edgelessCanvas]);

    React.useLayoutEffect(() => {
        syncDom();

        const zoomHandle = edgelessCanvas.onZoom(syncDom);
        const panHandle = edgelessCanvas.onPan(syncPosition);

        return () => {
            events.off(zoomHandle);
            events.off(panHandle);
        };
    }, [edgelessCanvas, events, syncDom, syncPosition]);

    const viewState = getViewState();

    return (
        <div
            ref={ref}
            className="edgeless__bg"
            style={{
                backgroundImage: viewState.backgroundImage,
                backgroundPosition: viewState.backgroundPosition,
                backgroundSize: viewState.backgroundSize,
                opacity: viewState.opacity,
            }}
        ></div>
    );
});

const EdgelessHost: React.FC<Props> = memo(({ stagePlugins }) => {
    const framework = useFramework();
    const { edgelessCanvas, events } = framework.service;
    const blur = useCanvasBlur();
    const blurFilter = getCanvasBlurFilter(blur);
    const canvasRef = useRef<HTMLCanvasElement>(edgelessCanvas.canvas);
    const initializedRef = useRef(false);
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const [isEdgelessMode, setIsEdgelessMode] = useState(edgelessCanvas.isEdgelessMode);

    useMountEffect(() => {
        const handle = edgelessCanvas.onRenderModeChange(() => setIsEdgelessMode(edgelessCanvas.isEdgelessMode));
        return () => events.off(handle);
    });

    useMountEffect(() => {
        if (!initializedRef.current && canvasContainerRef.current) {
            initializedRef.current = true;
            canvasContainerRef.current.appendChild(canvasRef.current);
        }
    });

    return (
        <>
            <div className="edgeless" style={blurFilter ? { filter: blurFilter } : undefined}>
                {isEdgelessMode && <GridDot />}
                <CustomLayer position="bottom" zIndex={1} />
                <div ref={canvasContainerRef} className="edgeless__scene"></div>
                <CustomLayer position="top" zIndex={10} />
                <DomRender />
                <AgentBadges />
            </div>
            {stagePlugins}
        </>
    );
});

EdgelessHost.displayName = 'EdgelessHost';

export { EdgelessHost };
